import {customElement, property, query, queryAll, state} from "lit/decorators.js";
import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import Styles from "./feedSpotlight.lit.scss";
import {EopImageSpinner} from "../../../page/elements/spinner";
import {DefaultFeedSource} from "../feedSource";
import {schedule} from "../../../common/utils/promises";
import {FeedEntry} from "../feedEntry";
import {VisitRecord} from "../contentsearch/visitRecord";
import {resolve} from "../../../container";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP, type BootstrapBreakpoint} from "../../../common/resolutionConstants";
import {Dictionary} from "../../../page/elements/dictionary";
import {KEYS} from "../../../common/utils/events";
import {TrackingEventName, TrackingEventType} from "../../../tracking/types";
import {ELEMENT_SCROLL_PADDING, ScrollService} from "../../../common/scroll";
import {ANIMATION_DURATION} from "../../../common/gridLayout";
import {Timeout} from "../../../common/timeout";
import {ResizeObserverFactory} from "../../../common/observation";

@customElement("eop-feed-spotlight")
export class EopFeedSpotlight extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "sources"})
    public sources: string;
    @property({attribute: "keywords"})
    public keywords: string;
    @property({attribute: "excluded-keywords"})
    public excludedKeywords: string;
    @property({attribute: "character-limit", type: Number})
    public characterLimit: number = 180;
    @query(".loading-entries-spinner")
    protected fetchSpinner: EopImageSpinner;
    @queryAll(".feed-tile")
    private items: NodeListOf<HTMLElement>;
    @query(".spotlight-container")
    private container: HTMLElement;
    @state()
    private feedEntries: FeedEntry[] = [];
    @state()
    private breakpoint: BootstrapBreakpoint | null = null;
    @state()
    private currentIndex: number = 0;

    private dictionary: Dictionary;
    private resizeObserver: ResizeObserver;

    public constructor(
        private timeout: Timeout = resolve(Timeout),
        private resolution: Resolution = resolve(Resolution),
        private visitRecord: VisitRecord = resolve(VisitRecord),
        private scrollService: ScrollService = resolve(ScrollService),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private feedSource: DefaultFeedSource = new DefaultFeedSource()
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.configureFeedSource();
        this.dictionary = Dictionary.of(this);
        this.resizeObserver = this.resizeObserverFactory.create(() => this.updateHeights());
        this.resolution.onBootstrapBreakpointChange(breakpoint => {
            this.breakpoint = breakpoint;
        });
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        void schedule(this.fetchSpinner.spinWhile(this.fetchFeedEntries())
            .then(() => {
                this.updateHeights();
                this.resizeObserver.observe(this.container);
            })).as("fetch");
    }

    private updateHeights(): void {
        this.items.forEach(item => this.setCalculatedHeight(item));
    }

    private setCalculatedHeight(item: HTMLElement): void {
        const descriptionHeight = item.querySelector<HTMLElement>(".description")!.clientHeight;
        const linkHeight = item.querySelector<HTMLElement>(".link-text")!.clientHeight;
        const feedImageHeight = item.querySelector<HTMLElement>(".feed-image")?.clientHeight ?? 0;
        item.style.setProperty("--collapsible-height", `${descriptionHeight + linkHeight}px`);
        item.style.setProperty("--collapsible-image-height", `${feedImageHeight}px`);
    }

    private configureFeedSource(): void {
        this.feedSource.configure({
            sources: this.sources?.parseAsJSON() ?? {},
            keywords: this.keywords?.parseCommaSeparated() ?? [],
            excludedKeywords: this.excludedKeywords?.parseCommaSeparated() ?? [],
            characterLimit: this.characterLimit,
            language: this.language
        });
    }

    private async fetchFeedEntries(): Promise<boolean> {
        const feeds = await this.feedSource.fetch(3, 0, this.visitRecord.getLastSessionDate());
        this.feedEntries = feeds
            .map((feed) => FeedEntry.fromResponse(feed)
                .inChannelContext(this.feedSource.channels())
                .beingRecentlyVisited(this.visitRecord.isVisited(feed.url)));
        return this.updateComplete;
    }

    public render(): TemplateResult {
        return html`
            <div class="spotlight-container">
                <eop-image-spinner class=loading-entries-spinner style="display: none;"></eop-image-spinner>
                ${this.renderTiles()}
            </div>`;
    }

    private renderTiles(): TemplateResult {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            return this.renderMobile();
        } else {
            return this.renderDesktop();
        }
    }

    private renderMobile(): TemplateResult {
        return html`
            <div class="feed-tiles">
                ${this.renderFeedTile(true)}
            </div>
        `;
    }

    private renderDesktop(): TemplateResult {
        return html`
            ${this.renderFeedTileImage(this.currentIndex)}
            <div class="feed-tiles">
                ${this.renderFeedTile(false)}
            </div>
        `;
    }

    private renderFeedTile(withImage: boolean): Array<TemplateResult> {
        return this.feedEntries.map((entry, index) => {
                const isLast = index === this.feedEntries.length - 1;
                return html`
                    <div class="feed-tile"
                         ?open=${(index === this.currentIndex)}
                         data-id=${index}
                         tabindex="0"
                         data-tracking-label=${entry.data.title}
                         data-tracking-event-name=${TrackingEventName.ACCORDION_INTERACT}
                         data-tracking-payload-label=${entry.data.title}
                         data-tracking-payload-type=${TrackingEventType.OPEN}
                         @click=${this.handleFeedTileClick}
                         @keydown=${this.handleFeedTileKeydown}
                    >
                        ${withImage ? this.renderFeedFeedImageContainer(index) : ""}
                        <div class="content">
                            <div class="metadata">
                                ${this.renderNewLabel(entry)}
                                <div class="news-type">${entry.data.newsType}</div>
                                <span class=toggle-chevron></span>
                            </div>
                            <div class=headline>${entry.data.title}</div>
                            <div class="feed-tile-content-container">
                                ${this.renderDescription(entry)}
                                ${this.renderArticleLink(entry, index)}
                            </div>
                        </div>
                    </div>
                    ${(!isLast && withImage ? html`
                        <hr>` : null)}
                `;
            }
        );
    }

    private renderFeedFeedImageContainer(index: number): TemplateResult {
        return html`
            <div class="feed-tile-image-container">
                ${this.renderFeedTileImage(index)}
            </div>`;
    }

    private renderFeedTileImage(index: number): TemplateResult {
        const entry = this.feedEntries[index];
        return html`
            <div class="feed-image">
                <eop-responsive-image
                        .imageSrc=${this.getImageUrl(entry)}
                        .imageAlt=${entry?.data.imageAltText}
                ></eop-responsive-image>
            </div>`;
    }

    private getImageUrl(entry: FeedEntry | null): string {
        if (!entry) {
            return "";
        }
        const bp = this.breakpoint || this.resolution.getBootstrapBreakpoint();
        if (bp === BOOTSTRAP.SM || bp === BOOTSTRAP.MD) {
            return entry.getImageUrlFor3x4Purpose();
        } else {
            return entry.getImageUrlFor7x4Purpose();
        }
    }

    private renderNewLabel(entry: FeedEntry): TemplateResult | null {
        if (!this.isNotVisited(entry)) {
            return null;
        }
        return html`<span class="label-new">${this.dictionary.translate("NEW_TAG")}</span>`;
    }

    private isNotVisited(entry: FeedEntry): boolean {
        return entry.data.isNew && entry.recentlyVisited === false;
    }

    private renderDescription(it: FeedEntry): TemplateResult | null {
        return html`
            <div class="description"><p>${it.data.description?.trim()}</p></div>`;
    }

    private renderArticleLink(entry: FeedEntry, index: number): TemplateResult | null {
        // TODO: Check if the link can be tacked the default way
        return html`
            <eop-page-overlay-link
                    href=${entry.data.url}
                    title=${entry.data.title}
                    data-tracking-label=${entry.data.title}
                    data-tracking-event-name=${TrackingEventName.TEXTLINK_INTERACT}
                    data-tracking-payload-label=${entry.data.title}
                    data-tracking-payload-type=${TrackingEventType.CLICK}
                    data-tracking-payload-link=${entry.data.url}
                    tabindex="0"
                    class="link"
                    data-id=${index}
                    @click=${this.handleFeedTileOpenClick}
            >
                <div class="link-text">${this.dictionary.translate("OPEN_ARTICLE")}</div>
            </eop-page-overlay-link>
        `;
    }

    private handleFeedTileClick(event: Event): void {
        const index = parseInt((event.currentTarget as HTMLElement).dataset.id || "0");
        if (this.currentIndex === index) {
            return;
        }
        this.scrollToFeedItem(index);
        this.currentIndex = index;
    }

    private handleFeedTileKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            this.handleFeedTileClick(event);
        }
    }

    private scrollToFeedItem(index: number): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            const openingItem = this.items[index];
            const itemBefore = this.items[this.currentIndex];
            const scrollMargin = index < this.currentIndex ? (2.5 * ELEMENT_SCROLL_PADDING) : itemBefore.clientHeight;
            void this.timeout.delay(() => {
                void this.scrollService.scrollToElement(openingItem, scrollMargin, ANIMATION_DURATION);
            });
        }
    }

    private handleFeedTileOpenClick(event: MouseEvent): void {
        const index = parseInt((event.currentTarget as HTMLElement).dataset.id ?? "0");
        const entry = this.feedEntries[index];
        if (this.isNotVisited(entry)) {
            this.visitRecord.addVisitedURL(entry.data.url);
        }
    }
}